<template>
  <div class="article-edit">
    <div class="page-header">
      <h2 class="page-title">{{ isEdit ? '编辑文章' : '新建文章' }}</h2>
      <el-button @click="$router.back()">返回</el-button>
    </div>

    <el-form :model="form" label-position="top">
    <div class="edit-layout">
      <!-- 左侧：编辑区 -->
      <div class="edit-left">
          <el-form-item label="标题">
            <el-input v-model="form.title" placeholder="请输入文章标题" />
          </el-form-item>

          <el-form-item label="摘要">
            <el-input v-model="form.summary" type="textarea" :rows="2" placeholder="文章摘要（可选）" />
          </el-form-item>

          <el-form-item label="内容（Markdown）">
            <div class="editor-wrapper">
              <div class="editor-tabs">
                <span :class="{ active: editMode === 'write' }" @click="editMode = 'write'">编辑</span>
                <span :class="{ active: editMode === 'preview' }" @click="editMode = 'preview'">预览</span>
              </div>
              <div v-show="editMode === 'write'">
                <el-input v-model="form.content_md" type="textarea" :rows="15"
                  placeholder="在这里输入 Markdown 内容..." />
              </div>
              <div v-show="editMode === 'preview'" class="preview-area markdown-body"
                v-html="previewHtml"></div>
            </div>
          </el-form-item>
      </div>

      <!-- 右侧：设置区 -->
      <div class="edit-right">
        <el-card shadow="never">
          <div slot="header"><strong>文章设置</strong></div>

          <el-form-item label="发布状态">
            <el-radio-group v-model="form.status">
              <el-radio label="draft">草稿</el-radio>
              <el-radio label="published">发布</el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="分类">
            <el-select v-model="form.category_id" placeholder="选择分类" clearable>
              <el-option v-for="cat in categories" :key="cat.id"
                :label="cat.name" :value="cat.id" />
            </el-select>
          </el-form-item>

          <el-form-item label="标签">
            <el-select v-model="form.tag_ids" multiple placeholder="选择标签">
              <el-option v-for="tag in tags" :key="tag.id"
                :label="tag.name" :value="tag.id" />
            </el-select>
          </el-form-item>

          <el-form-item label="置顶">
            <el-switch v-model="form.is_top" />
          </el-form-item>

          <el-form-item label="封面图">
            <el-input v-model="form.cover_image" placeholder="封面图URL（可选）" />
          </el-form-item>

          <el-button type="primary" :loading="saving" @click="saveArticle"
            style="width:100%">保存文章</el-button>
        </el-card>
      </div>
    </div>
    </el-form>
  </div>
</template>

<script>
import MarkdownIt from 'markdown-it'
import { adminApi, frontApi } from '../../utils/api'

const md = new MarkdownIt({ html: true, linkify: true, typographer: true })
md.enable(['table', 'strikethrough'])

export default {
  name: 'AdminArticleEdit',
  data() {
    return {
      form: {
        title: '',
        summary: '',
        content_md: '',
        status: 'draft',
        category_id: null,
        tag_ids: [],
        is_top: false,
        cover_image: ''
      },
      editMode: 'write',
      saving: false,
      categories: [],
      tags: []
    }
  },
  computed: {
    isEdit() {
      return this.$route.params.id
    },
    previewHtml() {
      return md.render(this.form.content_md || '')
    }
  },
  created() {
    this.loadCategoriesAndTags()
    if (this.isEdit) this.loadArticle()
  },
  methods: {
    async loadCategoriesAndTags() {
      try {
        const catRes = await frontApi.getCategories()
        const tagRes = await frontApi.getTags()
        this.categories = catRes.categories || []
        this.tags = tagRes.tags || []
      } catch (e) {
        console.error(e)
      }
    },
    async loadArticle() {
      try {
        const res = await adminApi.getArticles({ per_page: 100 })
        const article = (res.articles || []).find(a => a.id === parseInt(this.$route.params.id))
        if (article) {
          // 需要获取完整内容
          const detailRes = await frontApi.getArticle(article.slug)
          const full = detailRes.article
          this.form = {
            title: full.title,
            summary: full.summary,
            content_md: full.content_md,
            status: full.status,
            category_id: full.category ? full.category.id : null,
            tag_ids: full.tags ? full.tags.map(t => t.id) : [],
            is_top: full.is_top,
            cover_image: full.cover_image
          }
        }
      } catch (e) {
        console.error(e)
      }
    },
    async saveArticle() {
      if (!this.form.title || !this.form.content_md) {
        this.$message.warning('标题和内容不能为空')
        return
      }
      this.saving = true
      try {
        if (this.isEdit) {
          await adminApi.updateArticle(this.isEdit, this.form)
          this.$message.success('文章更新成功')
        } else {
          await adminApi.createArticle(this.form)
          this.$message.success('文章创建成功')
          this.$router.push('/admin/articles')
        }
      } catch (e) {
        this.$message.error('保存失败')
      }
      this.saving = false
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.page-title {
  font-size: 22px;
  font-weight: 600;
  color: #2c3e50;
}
.edit-layout {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 20px;
}
.edit-left {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e8e8e8;
}
.edit-right {
  position: sticky;
  top: 0;
}
.editor-wrapper {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}
.editor-tabs {
  display: flex;
  border-bottom: 1px solid #dcdfe6;
  background: #f5f5f5;
}
.editor-tabs span {
  padding: 8px 20px;
  cursor: pointer;
  font-size: 14px;
  color: #666;
}
.editor-tabs span.active {
  color: #409EFF;
  background: #fff;
  border-bottom: 2px solid #409EFF;
}
.preview-area {
  padding: 16px;
  min-height: 300px;
  max-height: 500px;
  overflow-y: auto;
}
@media (max-width: 768px) {
  .edit-layout {
    grid-template-columns: 1fr;
  }
}
</style>
